// stores/priceEntry.ts
// Sol menüdeki "Fiyat Girilecek" rozeti için ürün sayısı.
//
// Sayı iki yoldan güncellenir:
//   1) fetchCount()  — menü açılışında ve gerektiğinde API'den okunur
//   2) applyDelta()  — hücrede fiyat/stok kaydedildiğinde anında düzeltilir,
//                      böylece kullanıcı sayfayı yenilemeden sayının düştüğünü görür
import { defineStore } from 'pinia';
import { ref } from 'vue';
import http from '@/axios/axios.js';

interface CountableRow {
    stok?: number | string | null;
    fiyat_maliyet?: number | string | null;
    fiyat_bayi?: number | string | null;
    fiyat_musteri?: number | string | null;
    [key: string]: any;
}

// Fiyat Girilecek sayfasının varsayılan ölçütleri: fiyatı eksik + stokta olan ürünler
const countFilters = () => ({
    in_stock: { value: true, matchMode: 'equals' },
    needs_price: { value: true, matchMode: 'equals' }
});

// Ürün rozete dahil mi? (sayfadaki liste ölçütünün birebir karşılığı)
export const needsPriceEntry = (row: CountableRow, overrides: Record<string, any> = {}): boolean => {
    const valueOf = (field: string) => {
        const raw = field in overrides ? overrides[field] : row[field];
        return Number(raw ?? 0);
    };

    if (valueOf('stok') <= 0) return false;

    return valueOf('fiyat_maliyet') === 0 || valueOf('fiyat_bayi') === 0 || valueOf('fiyat_musteri') === 0;
};

export const usePriceEntryStore = defineStore('priceEntry', () => {
    const count = ref(0);
    const loaded = ref(false);

    const setCount = (value: number) => {
        count.value = Math.max(0, value);
        loaded.value = true;
    };

    const fetchCount = async () => {
        try {
            const response = await http.post('v1/products/filter/', {
                first: 0,
                rows: 1,
                filters: countFilters(),
                originalEvent: { page: 1, first: 0, rows: 1 }
            });

            setCount(response.data?.count ?? 0);
        } catch (error) {
            console.warn('Fiyat girilecek ürün sayısı alınamadı:', error);
        }
    };

    // Sayı henüz okunmadıysa (yetkisiz kullanıcı) yerel düzeltme yapılmaz
    const applyDelta = (delta: number) => {
        if (!loaded.value || !delta) return;
        count.value = Math.max(0, count.value + delta);
    };

    const reset = () => {
        count.value = 0;
        loaded.value = false;
    };

    return {
        count,
        loaded,
        setCount,
        fetchCount,
        applyDelta,
        reset
    };
});
